import { useEffect, useRef, useState, type CSSProperties, type RefObject } from "react";

/**
 * TheCore Studio — Segmented control.
 * Pill segmented selector — day filters, list/calendar toggles.
 * Controlled: pass `value` + `onChange`.
 *
 * The track scrolls horizontally when its options don't fit the app frame
 * (they are pills with `white-space: nowrap`, so there is no smaller size for
 * them to take). Rather than wrapping — which would break the single-track
 * metaphor — the track keeps one row and shows a soft fade over whichever edge
 * still has options hidden past it, so a clipped pill reads as "scroll me"
 * instead of "the layout is broken".
 *
 * Because the track is a scroll container, it is allowed to shrink inside flex
 * rows: its automatic minimum size no longer forces the whole row wider than
 * the sheet that contains it.
 */
export interface SegmentOption {
  label: string;
  value: string;
}

export interface SegmentedControlProps {
  /** Array of strings or {label, value}. */
  options: (string | SegmentOption)[];
  value: string;
  onChange?: (value: string) => void;
  /** @default 'md' */
  size?: "sm" | "md";
  /**
   * Show the edge fade while options are scrolled out of view. The fade only
   * appears when the track actually overflows. @default true
   */
  scrollHint?: boolean;
  /** Applied to the track itself (margins included). */
  style?: CSSProperties;
}

const HEIGHTS: Record<"sm" | "md", number> = { sm: 34, md: 42 };

export function SegmentedControl({
  options = [],
  value,
  onChange,
  size = "md",
  scrollHint = true,
  style = {},
}: SegmentedControlProps) {
  const items = options.map((o) => (typeof o === "string" ? { label: o, value: o } : o));
  const h = HEIGHTS[size] ?? HEIGHTS.md;
  const trackRef = useRef<HTMLDivElement>(null);
  const overflow = useOverflowEdges(
    trackRef,
    scrollHint,
    items.map((it) => it.label).join("|"),
  );

  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        // Shrinkable for the same reason the track is: a caller may drop us
        // into a flex row next to a title or a button.
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <div
        ref={trackRef}
        role="tablist"
        className="ds-segmented"
        style={{
          display: "inline-flex",
          padding: 4,
          gap: 2,
          // `overflow-x: auto` alone makes `overflow-y: visible` compute to
          // `auto` too, which clips the active pill's shadow and can add a
          // vertical scrollbar — pin the block axis instead.
          overflowX: "auto",
          overflowY: "hidden",
          // The edge fade carries the affordance, so the bar itself is hidden
          // (`scrollbar-width` covers current browsers, the class covers
          // older WebKit).
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          // Swiping past the last pill must not scroll the sheet behind us.
          overscrollBehaviorX: "contain",
          scrollSnapType: "x proximity",
          scrollPaddingInline: 4,
          background: "var(--bg-sunken)",
          borderRadius: "var(--radius-pill)",
          ...style,
        }}
      >
        {items.map((it) => {
          const active = it.value === value;
          return (
            <button
              key={it.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange?.(it.value)}
              style={{
                height: h,
                padding: "0 18px",
                border: "none",
                cursor: "pointer",
                borderRadius: "var(--radius-pill)",
                font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)",
                color: active ? "var(--text-on-brand)" : "var(--text-muted)",
                background: active ? "var(--brand)" : "transparent",
                boxShadow: active ? "var(--shadow-sm)" : "none",
                transition:
                  "background var(--dur-base) var(--ease-out), color var(--dur-base) var(--ease-out)",
                whiteSpace: "nowrap",
                // Pills keep their size; the track travels.
                flex: "none",
                scrollSnapAlign: "start",
              }}
            >
              {it.label}
            </button>
          );
        })}
      </div>

      {overflow.start && <EdgeFade side="start" />}
      {overflow.end && <EdgeFade side="end" />}
    </div>
  );
}

/** Soft fade over an edge of the track with options hidden past it. */
function EdgeFade({ side }: { side: "start" | "end" }) {
  const start = side === "start";
  return (
    <span
      aria-hidden="true"
      data-edge={side}
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        width: 36,
        pointerEvents: "none",
        [start ? "left" : "right"]: 0,
        borderTopLeftRadius: start ? "var(--radius-pill)" : 0,
        borderBottomLeftRadius: start ? "var(--radius-pill)" : 0,
        borderTopRightRadius: start ? 0 : "var(--radius-pill)",
        borderBottomRightRadius: start ? 0 : "var(--radius-pill)",
        background: start
          ? "linear-gradient(to right, var(--bg-sunken), transparent)"
          : "linear-gradient(to right, transparent, var(--bg-sunken))",
      }}
    />
  );
}

/**
 * Tracks which horizontal edges of `ref` have content scrolled out of view.
 * Returns `{start: false, end: false}` when there is nothing to scroll — or
 * when there is no layout engine to measure (jsdom), which keeps component
 * tests deterministic.
 */
function useOverflowEdges(
  ref: RefObject<HTMLDivElement | null>,
  enabled: boolean,
  /**
   * Identity of the labels being measured. A longer label can widen the
   * track's scroll width without changing its box, which ResizeObserver
   * never reports — a language switch would otherwise leave the fade stale.
   */
  signature: string,
): { start: boolean; end: boolean } {
  const [edges, setEdges] = useState({ start: false, end: false });

  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return;

    function measure() {
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      const next = {
        start: el.scrollLeft > 1,
        end: max > 1 && el.scrollLeft < max - 1,
      };
      // Keep the previous object when nothing changed: `scroll` fires often.
      setEdges((prev) =>
        prev.start === next.start && prev.end === next.end ? prev : next,
      );
    }

    measure();
    el.addEventListener("scroll", measure, { passive: true });
    // The track's box can change without the window resizing (a sheet
    // opening, a late webfont), so observe it where we can.
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    window.addEventListener("resize", measure);

    return () => {
      el.removeEventListener("scroll", measure);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [ref, enabled, signature]);

  return edges;
}
